import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HStack, FlatList, Text, Box, Pressable, Card, Icon,
} from '@gluestack-ui/themed';
import { Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { Plus } from 'lucide-react-native';
import AttachmentItem from '@/student/task/components/Attachments/AttachmentItem';
import AttachmentMenu from '@/student/task/components/Attachments/AttachmentMenu';
import AttachmentUploadButton from '@/student/task/components/Attachments/AttachmentUploadButton';
import { useAttachmentMutations } from '@/common/hooks/student/task/useAttachmentMutations';
import { useDialog } from '@/common/context/DialogContext';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import { sanitizeFilename } from '@/common/utils/fileUtils';
import { Attachment } from '@/student/task/types';

const getFileExtension = (filename: string) => filename.split('.').pop()?.toLowerCase() || '';

interface AttachmentsPanelProps {
  courseId: string;
  moduleId: string;
  taskId: string | null | undefined;
  attachments?: Attachment[];
  allowedFormats?: string[];
  readonly?: boolean;
  testID?: string;
}

/**
 * AttachmentsPanel handles file uploads and listing for a task.
 * Supports drag-and-drop on web and document picking on all platforms.
 */
const AttachmentsPanel: React.FC<AttachmentsPanelProps> = ({
  courseId,
  moduleId,
  taskId,
  attachments,
  allowedFormats = [],
  readonly = false,
  testID,
}) => {
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const { uploadAttachmentMutation, deleteAttachmentMutation } = useAttachmentMutations();
  const { reportApiError } = useApiErrorReporter();
  const allowedAttachmentExtensions = React.useMemo(
    () => allowedFormats.map((ext) => ext.toLowerCase()),
    [allowedFormats],
  );
  const allowedTypesLabel = React.useMemo(
    () => allowedAttachmentExtensions.map((type) => type.toUpperCase()).join(', '),
    [allowedAttachmentExtensions],
  );
  const uploadsEnabled = allowedAttachmentExtensions.length > 0 && !readonly;
  const [dragCounter, setDragCounter] = React.useState(0);
  const isDragging = dragCounter > 0;
  const dropRef = useRef<React.ComponentRef<typeof HStack>>(null);

  const isAttachmentValidationError = (error: unknown) => {
    if (!(error instanceof Error)) return false;
    const axiosError = error as { response?: { status?: number; data?: { error?: { code?: string } } } };
    const errorCode = axiosError.response?.data?.error?.code;
    return axiosError.response?.status === 400
      && (errorCode === 'SUBMISSION_ATTACHMENT_INVALID_FILETYPE' || errorCode === 'SUBMISSION_ATTACHMENT_MISSING_FILE');
  };

  const isAttachmentSizeError = (error: unknown) => {
    if (!(error instanceof Error)) return false;
    const axiosError = error as { response?: { status?: number; data?: { error?: { code?: string } } } };
    const errorCode = axiosError.response?.data?.error?.code;
    return axiosError.response?.status === 413 && errorCode === 'SUBMISSION_CONTENT_TOO_LARGE';
  };

  const showInvalidFiletypeToast = React.useCallback(() => {
    const fallbackMessage = allowedTypesLabel
      ? `Invalid file type. Allowed: ${allowedTypesLabel}.`
      : 'Attachments are disabled for this task.';
    showToastMessage(
      'error',
      t('errors.submission_attachment_invalid_filetype', { types: allowedTypesLabel, defaultValue: fallbackMessage }),
    );
  }, [showToastMessage, t, allowedTypesLabel]);

  const showImageSizeToast = React.useCallback(() => {
    showToastMessage(
      'error',
      t('errors.submission_attachment_image_too_large', { defaultValue: 'Image resolution is too large.' }),
    );
  }, [showToastMessage, t]);

  const handleUploadAttachment = React.useCallback(
    async (
      file: Blob | File | { uri: string; name: string; type: string },
      options?: { suppressToast?: boolean },
    ) => {
      if (!taskId || !uploadsEnabled) return false;
      const filename = file instanceof File ? file.name : ('name' in file ? file.name : '');
      const extension = filename ? getFileExtension(filename) : '';
      if (!extension || !allowedAttachmentExtensions.includes(extension)) {
        showInvalidFiletypeToast();
        return false;
      }
      try {
        await uploadAttachmentMutation.mutateAsync({
          courseId,
          moduleId,
          taskId,
          data: { file: file as Blob },
        });
        if (!options?.suppressToast) {
          showToastMessage('success', t('student.task.attachments.upload-success'));
        }
        return true;
      } catch (error) {
        if (isAttachmentValidationError(error)) {
          showInvalidFiletypeToast();
          return false;
        }
        if (isAttachmentSizeError(error)) {
          showImageSizeToast();
          return false;
        }
        reportApiError(error, 'Error uploading attachment');
        return false;
      }
    },
    [
      taskId,
      uploadsEnabled,
      allowedAttachmentExtensions,
      uploadAttachmentMutation,
      courseId,
      moduleId,
      t,
      showToastMessage,
      showInvalidFiletypeToast,
      showImageSizeToast,
      reportApiError,
    ],
  );

  const prepareFileUpload = React.useCallback(async (asset: DocumentPicker.DocumentPickerAsset) => {
    if (Platform.OS === 'web') {
      const webFile = asset.file;
      if (!webFile) return null;
      const targetFilename = sanitizeFilename(webFile.name);
      return new File([webFile], targetFilename, { type: webFile.type });
    }
    const targetFilename = sanitizeFilename(asset.name || asset.uri.substring(asset.uri.lastIndexOf('/') + 1));
    return {
      uri: asset.uri,
      type: asset.mimeType || 'application/octet-stream',
      name: targetFilename,
    };
  }, []);

  const pickDocument = React.useCallback(async () => {
    if (!uploadsEnabled) return;
    const pickerResult = await DocumentPicker.getDocumentAsync({
      type: '*/*',
      copyToCacheDirectory: true,
      multiple: true,
    });

    if (!pickerResult.canceled && pickerResult.assets?.length) {
      try {
        let successCount = 0;
        for (const asset of pickerResult.assets) {
          // eslint-disable-next-line no-await-in-loop
          const fileObject = await prepareFileUpload(asset);
          if (fileObject) {
            // eslint-disable-next-line no-await-in-loop
            const uploaded = await handleUploadAttachment(fileObject as Blob, { suppressToast: true });
            if (uploaded) successCount += 1;
          }
        }
        if (successCount > 0) {
          showToastMessage('success', t('student.task.attachments.upload-success'));
        }
      } catch (error) {
        reportApiError(error, 'Error uploading attachment');
      }
    }
  }, [uploadsEnabled, prepareFileUpload, handleUploadAttachment, showToastMessage, t, reportApiError]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !dropRef.current || !uploadsEnabled) {
      return undefined;
    }

    const el = dropRef.current as unknown as HTMLElement;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev + 1);
    };

    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => Math.max(0, prev - 1));
    };

    const onDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(0);

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        try {
          let successCount = 0;
          for (const file of files) {
            const sanitizedFile = new File([file], sanitizeFilename(file.name), { type: file.type });
            // eslint-disable-next-line no-await-in-loop
            const uploaded = await handleUploadAttachment(sanitizedFile, { suppressToast: true });
            if (uploaded) successCount += 1;
          }
          if (successCount > 0) {
            showToastMessage('success', t('student.task.attachments.upload-success'));
          }
        } catch (error) {
          reportApiError(error, 'Error uploading attachment');
        }
      }
    };

    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragenter', onDragEnter);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);

    return () => {
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('dragenter', onDragEnter);
      el.removeEventListener('dragleave', onDragLeave);
      el.removeEventListener('drop', onDrop);
    };
  }, [uploadsEnabled, handleUploadAttachment, showToastMessage, t, reportApiError]);

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!taskId) return;
    await deleteAttachmentMutation.mutateAsync(
      {
        courseId,
        moduleId,
        taskId,
        attachmentId,
      },
      {
        onSuccess: () => {
          showToastMessage('success', t('student.task.attachments.delete-success'));
        },
        onError: (error: unknown) => {
          reportApiError(error);
        },
      },
    );
  };

  if (!taskId) return null;
  if (!attachments?.length && allowedAttachmentExtensions.length === 0) return null;
  if (readonly && !attachments?.length) return null;

  return (
    <Pressable onPress={uploadsEnabled ? pickDocument : undefined} disabled={!uploadsEnabled}>
      <Box>
        <HStack
          ref={dropRef}
          w="100%"
          space="sm"
          minHeight={48}
          borderRadius="$md"
          mb="$1"
          overflow="hidden"
          alignItems="center"
          borderWidth={isDragging ? 2 : uploadsEnabled ? 1 : 0}
          borderStyle="dashed"
          borderColor={isDragging ? '$brandBase' : '$textHint'}
          backgroundColor={isDragging ? '$bgBrandSubtle' : readonly ? '$backgroundLight50' : 'transparent'}
          testID={testID}
        >
          {uploadsEnabled && !isDragging && (
            <AttachmentUploadButton
              onUploadAttachment={async (file) => {
                await handleUploadAttachment(file);
              }}
              readonly={!uploadsEnabled}
              onPress={pickDocument}
            />
          )}

          {isDragging ? (
            <Box flex={1} pr="$2" pointerEvents="none">
              <Text size="xs" color="$brandHover" fontWeight="bold" ml="$2">
                {t('student.task.attachments.drop-zone-hint')}
              </Text>
            </Box>
          ) : !attachments?.length ? (
            <Box flex={1} pr="$2">
              <Text size="xs" color="$textHint" ml="$2">
                {t('student.task.attachments.no-attachments-interactive', {
                  types: allowedTypesLabel,
                  defaultValue: `No attachments yet. Click here or drop files to upload. Allowed: ${allowedTypesLabel}`,
                })}
              </Text>
            </Box>
          ) : (
            <FlatList
              data={
                [...(attachments || []), ...(uploadsEnabled ? [{ id: 'add-more-trigger', isAddMore: true }] : [])] as (Attachment & { isAddMore?: boolean })[]
              }
              horizontal
              showsHorizontalScrollIndicator={false}
              keyExtractor={(item) => (item as Attachment).id}
              px="$0"
              sx={{
                _contentContainerStyle: {
                  alignItems: 'center',
                  paddingRight: 10,
                },
              }}
              renderItem={({ item }) => {
                const att = item as Attachment & { isAddMore?: boolean };
                if (att.isAddMore) {
                  return (
                    <Pressable onPress={pickDocument}>
                      <Card
                        w={60}
                        h={60}
                        bg="$backgroundLight100"
                        borderRadius="$md"
                        borderWidth={1}
                        borderStyle="dashed"
                        borderColor="$textHint"
                        alignItems="center"
                        justifyContent="center"
                        m="$1"
                      >
                        <Icon as={Plus} color="$textHint" size="lg" />
                      </Card>
                    </Pressable>
                  );
                }
                return (
                  <AttachmentMenu
                    key={att.id}
                    attachment={att}
                    isDeleting={
                      deleteAttachmentMutation.isPending
                      && deleteAttachmentMutation.variables?.attachmentId === att.id
                    }
                    onDeleteAttachment={() => handleDeleteAttachment(att.id)}
                    trigger={({ ...triggerProps }) => (
                      <AttachmentItem
                        key={att.id}
                        attachment={att}
                        isDeleting={
                          deleteAttachmentMutation.isPending
                          && deleteAttachmentMutation.variables?.attachmentId === att.id
                        }
                        triggerProps={triggerProps}
                      />
                    )}
                  />
                );
              }}
            />
          )}
        </HStack>

        {allowedTypesLabel && (
          <Text size="2xs" color="$textSubtle" ml="$2" mb="$2" testID="attachments-allowed-formats">
            {t('student.task.attachments.allowed-formats', {
              types: allowedTypesLabel,
              defaultValue: `Allowed formats: ${allowedTypesLabel}`,
            })}
          </Text>
        )}
      </Box>
    </Pressable>
  );
};

export default AttachmentsPanel;
